import type { HTMLAttributes } from 'react';
import { IconDecorator } from '@ardoq/decorators';
import { RuleType } from '../types';
import { APIEntityType } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { Checkbox } from '@ardoq/forms';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import styled, { css } from 'styled-components';
import {
  FORMATTING_RULE_LINE_HEIGHT as LINE_HEIGHT,
  getUnsupportedConditionRuleMessage,
  getUnsupportedConditionRuleLinkText,
} from '../../../consts';
import { type I18nLocale, t } from '@ardoq/i18n';
import { useFormattingContext } from '../useFormattingContext';
import { InfoNotification } from '@ardoq/status-ui';
import { InputsWrapper } from '../../../atoms';
import { Link } from '@ardoq/link';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { TextOverflow } from '@ardoq/popovers';
import { disabledCSS } from '@ardoq/style-helpers';
import { colors } from '@ardoq/design-tokens';

const getPropsLookup = (
  i18nLocale: I18nLocale
): Record<RuleType, { iconName: IconName; label: string }> => ({
  [APIEntityType.COMPONENT]: {
    label: t(
      i18nLocale,
      'perspectives:formattingRuleRow.componentLabel',
      'Component'
    ),
    iconName: IconName.COMPONENT,
  },
  [APIEntityType.TAG]: {
    label: t(i18nLocale, 'perspectives:formattingRuleRow.tagLabel', 'Tag'),
    iconName: IconName.TAG,
  },
  [APIEntityType.REFERENCE]: {
    label: t(
      i18nLocale,
      'perspectives:formattingRuleRow.referenceLabel',
      'Reference'
    ),
    iconName: IconName.REFERENCE,
  },
});
type FormattingRuleLabelProperties = {
  ruleType: RuleType;
  label?: string;
  isSidebar?: boolean;
  isDisabled?: boolean;
} & HTMLAttributes<HTMLDivElement>; // Pass popover ID so WithPopover hover works

const LabelContainer = styled.div<{
  $isSidebar?: boolean;
  $isDisabled?: boolean;
}>`
  width: 130px;
  ${({ $isSidebar }) => $isSidebar && 'flex: 1;'}
  white-space: normal;
  overflow-wrap: anywhere;
  min-height: ${LINE_HEIGHT}px;
  align-content: center;

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      ${disabledCSS};
    `}
`;

export const FormattingRuleLabel = ({
  ruleType,
  label: providedLabel,
  isSidebar = false,
  isDisabled = false,
  ...rest // HTMLAttributes for popover
}: FormattingRuleLabelProperties) => {
  const { i18nLocale } = useFormattingContext();
  const base = getPropsLookup(i18nLocale)[ruleType];
  const label = providedLabel ?? base.label;

  return (
    <LabelContainer $isSidebar={isSidebar} $isDisabled={isDisabled} {...rest}>
      <IconDecorator iconName={base.iconName}>
        <TextOverflow lineClamp={3}>{label}</TextOverflow>
      </IconDecorator>
    </LabelContainer>
  );
};

type InvertCheckboxProperties = {
  isInverted: boolean;
  onChange: (isInverted: boolean) => void;
};
export const InvertCheckbox = ({
  isInverted,
  onChange,
}: InvertCheckboxProperties) => {
  const { i18nLocale } = useFormattingContext();
  return (
    <div>
      <Checkbox
        dataTestId="isInverted-checkbox"
        isChecked={isInverted}
        onChange={() => onChange(!isInverted)}
      >
        <VisuallyHidden>
          {isInverted
            ? t(
                i18nLocale,
                'perspectives:formattingRuleRow.invertedSr',
                'Inverted '
              )
            : t(
                i18nLocale,
                'perspectives:formattingRuleRow.notInvertedSr',
                'Not inverted '
              )}
        </VisuallyHidden>
        {t(i18nLocale, 'perspectives:formattingRuleRow.inverse', 'Inverse')}
      </Checkbox>
    </div>
  );
};

const RuleWarningContainer = styled.div<{ $isMultiline?: boolean }>`
  width: 100%;
  ${({ $isMultiline }) => $isMultiline && 'white-space: pre-wrap;'}
`;

export const UnsupportedRuleWarning = ({
  isMultiline = false,
  i18nLocale,
}: {
  isMultiline: boolean;
  i18nLocale: I18nLocale;
}) => (
  <RuleWarningContainer $isMultiline={isMultiline}>
    <InfoNotification alignItems={'center'}>
      {getUnsupportedConditionRuleMessage(i18nLocale)}{' '}
      <Link
        href={KnowledgeBaseLink.CONDITIONAL_FORMATTING_IN_VIEWPOINT_BUILDER}
        target="_blank"
        rel="noopener noreferrer"
        hideIcon
        color={colors.textInfo}
        typography="text2"
      >
        {getUnsupportedConditionRuleLinkText(i18nLocale)}
      </Link>
    </InfoNotification>
  </RuleWarningContainer>
);

export const UnsupportedRuleContainer = styled(InputsWrapper).attrs({
  $isDisabled: true,
  $justify: 'end',
  inert: true,
})`
  flex: 1 1 100%;
`;

export const ColorPickerWrapper = styled.div<{ $hasWarning?: boolean }>`
  align-self: flex-start;
  border: 1px solid ${colors.grey300};
  border-radius: 4px;

  ${({ $hasWarning }) =>
    $hasWarning &&
    `
    border: 1px solid ${colors.borderWarning};
  `}
`;
